import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface FaqItem {
  q: string;
  a: string;
}

interface FaqCategory {
  id: string;
  label: string;
  icon: string;
  color: string;
  iconBg: string;
  tagBg: string;
  items: FaqItem[];
}

const FAQ_STATIC = [
  { id: 'apartment', catKey: 'cat1', icon: 'ri-building-2-line', color: 'text-[#05294A]', iconBg: 'bg-[#B8E2E8]', tagBg: 'bg-[#05294A]', count: 5 },
  { id: 'residential', catKey: 'cat2', icon: 'ri-home-heart-line', color: 'text-[#7499CE]', iconBg: 'bg-[#7499CE]/20', tagBg: 'bg-[#7499CE]', count: 5 },
  { id: 'commercial', catKey: 'cat3', icon: 'ri-store-2-line', color: 'text-[#7499CE]', iconBg: 'bg-[#7499CE]/20', tagBg: 'bg-[#7499CE]', count: 5 },
  { id: 'maintenance', catKey: 'cat4', icon: 'ri-hammer-line', color: 'text-slate-700', iconBg: 'bg-slate-100', tagBg: 'bg-slate-700', count: 5 },
];

function AccordionItem({ q, a, isOpen, onToggle, index, accentColor }: {
  q: string; a: string; isOpen: boolean; onToggle: () => void; index: number; accentColor: string;
}) {
  return (
    <div className={`border transition-all duration-300 ${isOpen ? 'border-[#92D7E8] bg-[#B8E2E8]/15' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
      <button onClick={onToggle} className="w-full flex items-start gap-4 p-5 text-left cursor-pointer">
        <span className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold mt-0.5 transition-colors duration-300 ${isOpen ? 'bg-[#05294A] text-white' : 'bg-slate-100 text-slate-500'}`}>
          {index + 1}
        </span>
        <span className={`flex-1 font-semibold text-sm leading-snug transition-colors duration-200 ${isOpen ? 'text-[#05294A]' : 'text-slate-900'}`}>{q}</span>
        <span className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${isOpen ? `${accentColor} text-white rotate-45` : 'bg-slate-100 text-slate-500'}`}>
          <i className="ri-add-line text-sm" />
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-400 ease-in-out ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="px-5 pb-5 pl-16">
          <p className="text-slate-600 text-sm leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const { t } = useTranslation();
  const [activeCatIdx, setActiveCatIdx] = useState(0);
  const [openItem, setOpenItem] = useState<number | null>(0);

  const FAQ_CATEGORIES: FaqCategory[] = FAQ_STATIC.map((s) => ({
    id: s.id,
    label: t(`faq.${s.catKey}.label`),
    icon: s.icon,
    color: s.color,
    iconBg: s.iconBg,
    tagBg: s.tagBg,
    items: Array.from({ length: s.count }, (_, i) => ({
      q: t(`faq.${s.catKey}.q${i + 1}`),
      a: t(`faq.${s.catKey}.a${i + 1}`),
    })),
  }));

  const activeCat = FAQ_CATEGORIES[activeCatIdx];

  const handleCatChange = (idx: number) => {
    setActiveCatIdx(idx);
    setOpenItem(0);
  };

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const allItems = FAQ_CATEGORIES.flatMap((cat) =>
      cat.items.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } }))
    );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: allItems });
    const existing = document.getElementById('faq-schema');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => { document.getElementById('faq-schema')?.remove(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCatIdx, t]);

  return (
    <section id="faq" className="py-16 md:py-24 bg-[#F0F7FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-12">
          <div>
            <span className="text-[#05294A] text-xs tracking-widest uppercase font-semibold">{t('faq.label')}</span>
            <h3 className="text-3xl md:text-5xl font-bold text-slate-900 mt-3 leading-tight">
              {t('faq.heading')}<br /><span className="text-[#05294A]">{t('faq.headingEm')}</span>
            </h3>
          </div>
          <p className="text-slate-500 text-base md:max-w-sm md:text-right leading-relaxed">{t('faq.desc')}</p>
        </div>

        <div className="flex flex-wrap gap-2 md:gap-3 mb-8 md:mb-10">
          {FAQ_CATEGORIES.map((cat, idx) => (
            <button
              key={cat.id}
              onClick={() => handleCatChange(idx)}
              className={`flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeCatIdx === idx
                  ? 'bg-[#67CBFF] text-[#05294A]'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-[#92D7E8]'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${cat.icon} text-base`} /></div>
              <span>{cat.label}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeCatIdx === idx ? 'bg-[#05294A]/15 text-[#05294A]' : 'bg-slate-100 text-slate-500'}`}>{cat.items.length}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2 flex flex-col gap-3">
            {activeCat.items.map((item, idx) => (
              <AccordionItem
                key={`${activeCat.id}-${idx}`}
                q={item.q} a={item.a}
                isOpen={openItem === idx}
                onToggle={() => setOpenItem(openItem === idx ? null : idx)}
                index={idx}
                accentColor={activeCat.tagBg}
              />
            ))}
          </div>

          <div className="flex flex-col gap-4 md:gap-5">
            <div className="p-5 md:p-6 bg-[#05294A] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className={`w-11 h-11 md:w-12 md:h-12 flex items-center justify-center ${activeCat.iconBg} ${activeCat.color} mb-4 relative z-10`}>
                <i className={`${activeCat.icon} text-lg md:text-xl`} />
              </div>
              <h4 className="text-white font-bold text-base mb-2 relative z-10">{activeCat.label}</h4>
              <p className="text-[#92D7E8] text-sm leading-relaxed mb-4 md:mb-5 relative z-10">
                {activeCat.items.length}{t('faq.sidebar.questions')}
              </p>
              <div className="flex items-center gap-2 relative z-10">
                <span className="w-2 h-2 rounded-full bg-[#67CBFF]" />
                <span className="text-[#67CBFF] text-xs font-medium">{activeCat.items.length} {t('faq.sidebar.questions')}</span>
              </div>
            </div>

            <div className="border border-slate-200 bg-white p-5 md:p-6">
              <p className="text-slate-900 font-bold text-sm mb-1">{t('faq.sidebar.stillHave')}</p>
              <p className="text-slate-500 text-xs leading-relaxed mb-4 md:mb-5">{t('faq.sidebar.stillHaveDesc')}</p>
              <div className="flex flex-col gap-3">
                <a href="tel:4702225777" className="flex items-center gap-3 p-3 md:p-3.5 bg-slate-50 border border-slate-200 hover:border-[#92D7E8] hover:bg-[#B8E2E8]/20 transition-all duration-200 cursor-pointer group">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#B8E2E8] text-[#05294A] group-hover:bg-[#05294A] group-hover:text-white transition-all duration-200 shrink-0">
                    <i className="ri-phone-line text-sm" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">{t('faq.sidebar.call')}</p>
                    <p className="text-slate-900 text-sm font-semibold">470-222-5777</p>
                  </div>
                </a>
                <a href="mailto:office@fmacrew.com" className="flex items-center gap-3 p-3 md:p-3.5 bg-slate-50 border border-slate-200 hover:border-[#92D7E8] hover:bg-[#B8E2E8]/20 transition-all duration-200 cursor-pointer group">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#B8E2E8] text-[#05294A] group-hover:bg-[#05294A] group-hover:text-white transition-all duration-200 shrink-0">
                    <i className="ri-mail-line text-sm" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">{t('faq.sidebar.email')}</p>
                    <p className="text-slate-900 text-sm font-semibold">office@fmacrew.com</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#05294A] to-[#7499CE] p-5 md:p-6 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-white/15 mb-4">
                <i className="ri-file-list-3-line text-xl" />
              </div>
              <p className="font-bold text-base mb-1">{t('faq.sidebar.ready')}</p>
              <p className="text-[#B8E2E8] text-xs leading-relaxed mb-4 md:mb-5">{t('faq.sidebar.readyDesc')}</p>
              <button onClick={scrollToContact}
                className="w-full py-3 bg-white text-[#05294A] rounded-lg text-sm font-bold hover:bg-[#B8E2E8]/50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <span>{t('faq.sidebar.btn')}</span>
                <i className="ri-arrow-right-line" />
              </button>
            </div>

            <div className="border border-slate-200 bg-white p-4 md:p-5">
              <p className="text-slate-500 text-xs uppercase tracking-wide font-semibold mb-3">{t('faq.sidebar.otherTopics')}</p>
              <div className="flex flex-col gap-2">
                {FAQ_CATEGORIES.filter((_, i) => i !== activeCatIdx).map((cat) => {
                  const originalIdx = FAQ_CATEGORIES.indexOf(cat);
                  return (
                    <button key={cat.id} onClick={() => handleCatChange(originalIdx)}
                      className="flex items-center gap-3 p-3 bg-white border border-slate-200 hover:border-[#92D7E8] transition-all duration-200 cursor-pointer group text-left">
                      <div className={`w-7 h-7 flex items-center justify-center ${cat.iconBg} ${cat.color} shrink-0`}>
                        <i className={`${cat.icon} text-xs`} />
                      </div>
                      <span className="text-slate-700 text-xs font-medium group-hover:text-[#05294A] transition-colors">{cat.label}</span>
                      <i className="ri-arrow-right-s-line text-slate-300 ml-auto group-hover:text-[#67CBFF] transition-colors" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
